/**
 * Subscription Plans Configuration
 * Updated structure: FREE → PREMIUM → PRO (removed BASIC tier)
 */
export const SUBSCRIPTION_PLANS = {
  FREE: {
    id: "free",
    name: "Free",
    tagline: "Try the Method",
    description: "Perfect for getting started with the shadowing method",
    monthlyPrice: 0,
    yearlyPrice: 0,
    stripePriceIds: {
      monthly: null,
      yearly: null,
    },
    displayOrder: 1,
    isDefault: true,
    isPublic: true,
    isMostPopular: false,
    features: {
      // Core features
      TEXT_CREATION_LIMIT: 5, // Five AI generations total on Free.
      ACCESS_ALL_VOICES: false, // Only 2 standard voices per language
      CREATE_PRIVATE_TEXTS: false,

      // Shadowing features
      SHADOWING_DAILY_TIME: 1200, // 20 minutes per day (1200 seconds)

      // Dictionary features
      DICTIONARY_WORD_LIMIT: 100, // Save up to 100 vocabulary words
      DICTIONARY_AI_SUGGESTIONS: false,

      // Dictation features
      DICTATION_SESSIONS_LIMIT: 3, // 3 dictation exercises per day
      DICTATION_ADVANCED_SETTINGS: false,

      // Future features
      COMPOSITION_MODULE_ACCESS: false,
      COMPOSITION_DAILY_ATTEMPTS: 0,
      AI_PRONUNCIATION_ANALYSIS: false,

      // Premium features
      PRIORITY_PROCESSING: false,
      ADVANCED_ANALYTICS: false,
      API_ACCESS: false,
    },
  },

  PREMIUM: {
    id: "premium",
    name: "Premium",
    tagline: "Everything You Need",
    description: "Everything you need for effective language learning",
    monthlyPrice: 999, // $9.99
    yearlyPrice: 5999, // $59.99 (save 50%)
    stripePriceIds: {
      monthly: "price_premium_monthly",
      yearly: "price_premium_yearly",
    },
    displayOrder: 2,
    isDefault: false,
    isPublic: true,
    isMostPopular: true, // ⭐ Most Popular
    features: {
      // Core features
      TEXT_CREATION_LIMIT: 30, // 30 AI texts every month
      ACCESS_ALL_VOICES: false, // Standard voices only (not premium voices)
      CREATE_PRIVATE_TEXTS: true,

      // Shadowing features
      SHADOWING_DAILY_TIME: -1, // Unlimited shadowing practice

      // Dictionary features
      DICTIONARY_WORD_LIMIT: -1, // Unlimited vocabulary
      DICTIONARY_AI_SUGGESTIONS: false,

      // Dictation features
      DICTATION_SESSIONS_LIMIT: -1, // Unlimited dictations
      DICTATION_ADVANCED_SETTINGS: true,

      // Future features
      COMPOSITION_MODULE_ACCESS: true,
      COMPOSITION_DAILY_ATTEMPTS: -1,
      AI_PRONUNCIATION_ANALYSIS: false,

      // Premium features
      PRIORITY_PROCESSING: false,
      ADVANCED_ANALYTICS: false,
      API_ACCESS: false,
    },
  },

  PRO: {
    id: "pro",
    name: "Pro",
    tagline: "For Power Learners",
    description: "For teachers, polyglots, and power learners",
    monthlyPrice: 1999, // $19.99
    yearlyPrice: 11999, // $119.99 (save 50%)
    stripePriceIds: {
      monthly: "price_pro_monthly",
      yearly: "price_pro_yearly",
    },
    displayOrder: 3,
    isDefault: false,
    isPublic: true, // Now public (was hidden before)
    isMostPopular: false,
    features: {
      // Core features
      TEXT_CREATION_LIMIT: 100, // 100 AI texts per month
      ACCESS_ALL_VOICES: true, // All voices (standard + premium)
      CREATE_PRIVATE_TEXTS: true,

      // Shadowing features
      SHADOWING_DAILY_TIME: -1, // Unlimited

      // Dictionary features
      DICTIONARY_WORD_LIMIT: -1, // Unlimited
      DICTIONARY_AI_SUGGESTIONS: true,

      // Dictation features
      DICTATION_SESSIONS_LIMIT: -1, // Unlimited
      DICTATION_ADVANCED_SETTINGS: true,

      // Future features
      COMPOSITION_MODULE_ACCESS: true,
      COMPOSITION_DAILY_ATTEMPTS: -1,
      AI_PRONUNCIATION_ANALYSIS: true,

      // Premium features
      PRIORITY_PROCESSING: true, // Priority AI generation
      ADVANCED_ANALYTICS: true,
      API_ACCESS: true, // Early access to new features
      PRIORITY_SUPPORT: true, // Priority support
    },
  },
} as const;

export type SubscriptionPlanId = keyof typeof SUBSCRIPTION_PLANS;
export type SubscriptionPlan = (typeof SUBSCRIPTION_PLANS)[SubscriptionPlanId];

/** * Feature definitions for subscription plans
 */
export const FEATURE_DEFINITIONS = {
  TEXT_CREATION_LIMIT: {
    name: "AI Text Generation",
    description: "Number of AI-generated texts per month",
    type: "limit" as const,
  },
  ACCESS_ALL_VOICES: {
    name: "Voice Access",
    description: "Access to premium voices",
    type: "boolean" as const,
  },
  CREATE_PRIVATE_TEXTS: {
    name: "Private Texts",
    description: "Ability to create private texts",
    type: "boolean" as const,
  },
  SHADOWING_DAILY_TIME: {
    name: "Shadowing Time",
    description: "Daily shadowing practice time in seconds",
    type: "limit" as const,
  },
  DICTIONARY_WORD_LIMIT: {
    name: "Dictionary Size",
    description: "Maximum words in personal dictionary",
    type: "limit" as const,
  },
  DICTIONARY_AI_SUGGESTIONS: {
    name: "AI Suggestions",
    description: "AI-powered vocabulary suggestions",
    type: "boolean" as const,
  },
  DICTATION_SESSIONS_LIMIT: {
    name: "Dictation Sessions",
    description: "Dictation exercises per day",
    type: "limit" as const,
  },
  DICTATION_ADVANCED_SETTINGS: {
    name: "Advanced Dictation",
    description: "Advanced dictation settings",
    type: "boolean" as const,
  },
  COMPOSITION_MODULE_ACCESS: {
    name: "Composition Module",
    description: "Access to composition module",
    type: "boolean" as const,
  },
  COMPOSITION_DAILY_ATTEMPTS: {
    name: "Composition Attempts",
    description: "Daily composition attempts",
    type: "limit" as const,
  },
  AI_PRONUNCIATION_ANALYSIS: {
    name: "AI Pronunciation",
    description: "AI pronunciation analysis",
    type: "boolean" as const,
  },
  PRIORITY_PROCESSING: {
    name: "Priority Processing",
    description: "Priority AI generation",
    type: "boolean" as const,
  },
  ADVANCED_ANALYTICS: {
    name: "Advanced Analytics",
    description: "Advanced learning analytics",
    type: "boolean" as const,
  },
  API_ACCESS: {
    name: "API Access",
    description: "Early access to new features",
    type: "boolean" as const,
  },
  PRIORITY_SUPPORT: {
    name: "Priority Support",
    description: "Priority customer support",
    type: "boolean" as const,
  },
} as const;

/**
 * Get all available plans
 */
export function getAllPlans(): SubscriptionPlan[] {
  return Object.values(SUBSCRIPTION_PLANS);
}

/**
 * Get features by category
 */
export function getFeaturesByCategory(): Record<
  string,
  (typeof FEATURE_DEFINITIONS)[keyof typeof FEATURE_DEFINITIONS][]
> {
  return {
    core: [
      FEATURE_DEFINITIONS.TEXT_CREATION_LIMIT,
      FEATURE_DEFINITIONS.ACCESS_ALL_VOICES,
      FEATURE_DEFINITIONS.CREATE_PRIVATE_TEXTS,
    ],
    practice: [
      FEATURE_DEFINITIONS.SHADOWING_DAILY_TIME,
      FEATURE_DEFINITIONS.DICTATION_SESSIONS_LIMIT,
      FEATURE_DEFINITIONS.DICTATION_ADVANCED_SETTINGS,
    ],
    dictionary: [
      FEATURE_DEFINITIONS.DICTIONARY_WORD_LIMIT,
      FEATURE_DEFINITIONS.DICTIONARY_AI_SUGGESTIONS,
    ],
    advanced: [
      FEATURE_DEFINITIONS.COMPOSITION_MODULE_ACCESS,
      FEATURE_DEFINITIONS.COMPOSITION_DAILY_ATTEMPTS,
      FEATURE_DEFINITIONS.AI_PRONUNCIATION_ANALYSIS,
      FEATURE_DEFINITIONS.PRIORITY_PROCESSING,
      FEATURE_DEFINITIONS.ADVANCED_ANALYTICS,
      FEATURE_DEFINITIONS.API_ACCESS,
      FEATURE_DEFINITIONS.PRIORITY_SUPPORT,
    ],
  };
}

/**
 * Format feature value for display
 */
export function formatFeatureValue(
  value: number | boolean | string | null | undefined,
): string {
  if (value === null || value === undefined || value === false) {
    return "✗";
  }
  if (value === true) {
    return "✓";
  }
  if (typeof value === "number") {
    if (value === -1 || value === 999999) {
      return "∞";
    }
    return value.toString();
  }
  return String(value);
}

/** * Marketing messages and feature descriptions for UI
 */
export const PLAN_MARKETING = {
  FREE: {
    features: [
      "5 AI-generated texts in total",
      "Basic text library",
      "2 standard voices per language",
      "20 minutes daily practice",
      "Save up to 100 vocabulary words",
      "3 dictation exercises per day",
    ],
    badge: null,
  },
  PREMIUM: {
    features: [
      "30 AI texts every month",
      "Full text library (basic + extended)",
      "Standard voices",
      "Unlimited shadowing practice",
      "Unlimited vocabulary",
      "Unlimited dictations",
      "No ads",
    ],
    badge: "⭐ Most Popular",
  },
  PRO: {
    features: [
      "Everything in Premium",
      "100 AI texts per month",
      "All voices (standard + premium)",
      "Priority AI generation",
      "Priority support",
      "Early access to new features",
    ],
    badge: "💎 Pro",
  },
} as const;
